import React, { useState, useEffect, useRef } from 'react';
import { Button } from '../../../components/ui/Button';
import { Play, Square, Pause } from 'lucide-react';
import { format } from 'date-fns';

interface TimeTimerProps {
  onStop: (duration: number, startTime: Date, endTime: Date) => void;
  onSave?: (duration: number) => void;
}

export const TimeTimer: React.FC<TimeTimerProps> = ({ onStop }) => {
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (isRunning && startTime) {
      intervalRef.current = setInterval(() => {
        const now = new Date();
        const diff = Math.floor((now.getTime() - startTime.getTime()) / 1000); // seconds
        setElapsed(diff);
      }, 1000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isRunning, startTime]);

  const handleStart = () => {
    const now = new Date();
    setStartTime(now);
    setIsRunning(true);
    setElapsed(0);
  };

  const handlePause = () => {
    setIsRunning(false);
  };

  const handleResume = () => {
    if (startTime) {
      // Adjust start time to account for elapsed time
      const adjustedStart = new Date(Date.now() - elapsed * 1000);
      setStartTime(adjustedStart);
      setIsRunning(true);
    }
  };

  const handleStop = () => {
    if (startTime) {
      const endTime = new Date();
      const duration = Math.floor((endTime.getTime() - startTime.getTime()) / (1000 * 60)); // minutes
      onStop(duration, startTime, endTime);
      setIsRunning(false);
      setStartTime(null);
      setElapsed(0);
    }
  };

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0 && minutes > 0) {
      return `${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${minutes}m`;
    }
  };

  return (
    <div className="border rounded-lg p-6 bg-white shadow-sm">
      <div className="text-center mb-6">
        <div className="text-4xl font-mono font-bold mb-2">
          {formatTime(elapsed)}
        </div>
        <p className="text-sm text-gray-600">
          {startTime && format(new Date(startTime), 'PPp')}
        </p>
        {elapsed > 0 && (
          <p className="text-xs text-gray-500 mt-1">
            {formatDuration(elapsed)} elapsed
          </p>
        )}
      </div>

      <div className="flex justify-center gap-2">
        {!isRunning && !startTime && (
          <Button onClick={handleStart} size="lg">
            <Play className="mr-2 h-5 w-5" />
            Start Timer
          </Button>
        )}

        {isRunning && (
          <>
            <Button variant="outline" onClick={handlePause} size="lg">
              <Pause className="mr-2 h-5 w-5" />
              Pause
            </Button>
            <Button variant="destructive" onClick={handleStop} size="lg">
              <Square className="mr-2 h-5 w-5" />
              Stop
            </Button>
          </>
        )}

        {!isRunning && startTime && (
          <>
            <Button onClick={handleResume} size="lg">
              <Play className="mr-2 h-5 w-5" />
              Resume
            </Button>
            <Button variant="destructive" onClick={handleStop} size="lg">
              <Square className="mr-2 h-5 w-5" />
              Stop & Save
            </Button>
          </>
        )}
      </div>
    </div>
  );
};
